import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Editor from "@monaco-editor/react";
import { useEditorTheme } from "../theme.js";
import QuestionEditor, { McqOptions, Validator } from "../components/QuestionEditor.js";
import LibraryImportModal from "../components/LibraryImportModal.js";
import {
  api,
  type Assignment,
  type AssignmentPart,
  type TestCase,
} from "../api.js";

/** Teacher view: assignment settings, starter code, and the test-case editor. */
export default function TeacherAssignment() {
  const editorTheme = useEditorTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [a, setA] = useState<Assignment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * Test cases used to save one at a time, from their own buttons, while the
   * page's Save covered only the assignment. Editing three cases and pressing
   * Save lost two of them silently. Now every edit is tracked and the one Save
   * writes all of it.
   */
  const [dirtyFields, setDirtyFields] = useState(false);
  const [dirtyCases, setDirtyCases] = useState<Set<string>>(new Set());
  const dirty = dirtyFields || dirtyCases.size > 0;

  const reload = useCallback(() => {
    if (!id) return;
    api
      .get<{ assignment: Assignment; isStaff: boolean }>(`/api/assignments/${id}`)
      .then((r) => {
        if (!r.isStaff) {
          navigate(`/assignments/${id}`, { replace: true });
          return;
        }
        setA(r.assignment);
      })
      .catch((e) => setError(e.message));
  }, [id, navigate]);

  useEffect(reload, [reload]);

  // The browser's own guard. It only fires on reload or close, so the in-app
  // links carry their own check below.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  function patch(fields: Partial<Assignment>) {
    setA((prev) => (prev ? { ...prev, ...fields } : prev));
    setDirtyFields(true);
  }

  function updateTestCase(tcId: string, fields: Partial<TestCase>) {
    setA((prev) =>
      prev
        ? { ...prev, testCases: prev.testCases.map((t) => (t.id === tcId ? { ...t, ...fields } : t)) }
        : prev
    );
    setDirtyCases((prev) => new Set(prev).add(tcId));
  }

  async function save() {
    if (!a || !id) return;
    setSaving(true);
    setError(null);
    setStatus(null);
    try {
      await api.patch(`/api/assignments/${id}`, {
        title: a.title,
        description: a.description,
        inputFormat: a.inputFormat,
        outputFormat: a.outputFormat,
        starterCode: a.starterCode,
        maxMarks: a.maxMarks,
        dueAt: a.dueAt ? new Date(a.dueAt).toISOString() : null,
        allowLate: a.allowLate,
        published: a.published,
        timeLimitMs: a.timeLimitMs,
        memoryLimitKb: a.memoryLimitKb,
        outputLimitKb: a.outputLimitKb,
      });

      const pending = a.testCases.filter((tc) => dirtyCases.has(tc.id));
      for (const tc of pending) {
        await api.patch(`/api/testcases/${tc.id}`, {
          name: tc.name,
          stdin: tc.stdin ?? "",
          expectedStdout: tc.expectedStdout ?? "",
          weight: tc.weight,
          hidden: tc.hidden,
          matchMode: tc.matchMode ?? "TRIMMED",
          orderIndex: tc.orderIndex,
        });
      }

      setDirtyFields(false);
      setDirtyCases(new Set());
      setStatus(
        pending.length > 0
          ? `Saved, including ${pending.length} test case${pending.length === 1 ? "" : "s"}.`
          : "Saved."
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  /**
   * Library sharing saves immediately rather than waiting for Save: it is a
   * visibility change other faculty depend on, not a draft edit.
   */
  async function shareToLibrary(libraryVisible: boolean) {
    if (!id) return;
    setA((prev) => (prev ? { ...prev, libraryVisible } : prev));
    try {
      await api.patch(`/api/assignments/${id}/library`, { libraryVisible });
      setStatus(
        libraryVisible
          ? "Shared. Other faculty can now copy this into their courses; their copies are independent of yours."
          : "Removed from the library. Copies already made are unaffected."
      );
    } catch (e) {
      setA((prev) => (prev ? { ...prev, libraryVisible: !libraryVisible } : prev));
      setError(e instanceof Error ? e.message : "Could not change library sharing.");
    }
  }

  async function addTestCase(copyOf?: TestCase) {
    if (!id || !a) return;
    try {
      const r = await api.post<{ testCase: TestCase }>(`/api/assignments/${id}/testcases`, {
        name: copyOf ? `${copyOf.name} (copy)` : `Test ${a.testCases.length + 1}`,
        stdin: copyOf?.stdin ?? "",
        expectedStdout: copyOf?.expectedStdout ?? "",
        weight: copyOf?.weight ?? 1,
        hidden: copyOf?.hidden ?? true,
        matchMode: copyOf?.matchMode ?? "TRIMMED",
        orderIndex: a.testCases.length,
      });
      setA((prev) => (prev ? { ...prev, testCases: [...prev.testCases, r.testCase] } : prev));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not add the test case.");
    }
  }

  /** Swap a case with its neighbour and persist both positions immediately. */
  async function move(index: number, delta: number) {
    if (!a) return;
    const target = index + delta;
    if (target < 0 || target >= a.testCases.length) return;

    const reordered = [...a.testCases];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const renumbered = reordered.map((tc, i) => ({ ...tc, orderIndex: i }));
    setA((prev) => (prev ? { ...prev, testCases: renumbered } : prev));

    try {
      await Promise.all(
        [renumbered[index], renumbered[target]].map((tc) =>
          api.patch(`/api/testcases/${tc.id}`, { orderIndex: tc.orderIndex })
        )
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reorder the test cases.");
    }
  }

  async function deleteTestCase(tcId: string) {
    if (!a || !confirm("Delete this test case?")) return;
    try {
      await api.del(`/api/testcases/${tcId}`);
      setA((prev) =>
        prev ? { ...prev, testCases: prev.testCases.filter((t) => t.id !== tcId) } : prev
      );
      setDirtyCases((prev) => {
        const next = new Set(prev);
        next.delete(tcId);
        return next;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the test case.");
    }
  }

  /**
   * Delete the thing being edited and leave. A part returns to its sheet,
   * anything else to the course list -- staying on the page of a record that no
   * longer exists is the one outcome nobody wants.
   */
  async function deleteAssignment() {
    if (!a || !id) return;
    const parts = a.parts.length;
    const what = a.parentId ? "question" : parts > 0 ? "sheet" : "assignment";
    if (
      !confirm(
        `Delete "${a.title}"?\n\n` +
          (parts > 0 ? `Its ${parts} question(s) go with it. ` : "") +
          `This cannot be undone. If students have submitted, unpublish this ${what} instead.`
      )
    )
      return;
    setError(null);
    try {
      await api.del(`/api/assignments/${id}`);
      // Past the point of no return: the unsaved-changes guard has nothing left
      // to protect, and would only ask about a record that is gone.
      setDirtyFields(false);
      setDirtyCases(new Set());
      navigate(a.parentId ? `/assignments/${a.parentId}/edit` : "/", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete this.");
    }
  }

  const leaveGuard = useCallback(
    (e: React.MouseEvent) => {
      if (dirty && !confirm("You have unsaved changes. Leave without saving?")) e.preventDefault();
    },
    [dirty]
  );

  if (error && !a) return <div className="page"><div className="error-banner">{error}</div></div>;
  if (!a) return <div className="page muted">Loading…</div>;

  // datetime-local wants "YYYY-MM-DDTHH:mm" in local time, not an ISO string.
  const dueLocal = a.dueAt ? toLocalInput(new Date(a.dueAt)) : "";
  const isMcq = a.kind === "MCQ";
  /** A sheet: its questions live in parts, and it has none of its own. */
  const isSheet = a.parts.length > 0;
  /** A question inside a sheet, reached from it. */
  const isPart = !!a.parentId;

  return (
    <div className="page">
      {a.parent ? (
        <Link
          to={`/assignments/${a.parent.id}/edit`}
          className="muted"
          style={{ fontSize: 13 }}
          onClick={leaveGuard}
        >
          ← {a.parent.title}
        </Link>
      ) : (
        <Link to="/assignments" className="muted" style={{ fontSize: 13 }} onClick={leaveGuard}>
          ← All assignments
        </Link>
      )}

      <div className="between" style={{ margin: "10px 0 16px" }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>
          {isPart ? "Edit question" : isSheet ? "Edit sheet" : "Edit assignment"}
        </h1>
        <div className="row">
          {dirty && <span className="badge warn">unsaved</span>}
          <Link to={`/assignments/${a.id}/submissions`} onClick={leaveGuard}>
            <button>Submissions</button>
          </Link>
          <Link to={`/assignments/${a.id}`} onClick={leaveGuard}><button>Preview</button></Link>
          <button className="danger" onClick={deleteAssignment}>Delete</button>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      {a.published && !isMcq && a.testCases.length === 0 && (
        <div className="notice">
          This assignment is published but has no test cases, so students cannot submit. Add at least one.
        </div>
      )}
      {a.published && isMcq && a.options.filter((o) => o.correct).length === 0 && (
        <div className="notice">
          This question is published but no option is marked correct, so every answer scores zero.
        </div>
      )}

      <div className="card">
        <label>
          <span>Title</span>
          <input value={a.title} onChange={(e) => patch({ title: e.target.value })} />
        </label>

        <p className="muted" style={{ fontSize: 13, marginTop: -6 }}>
          {isSheet
            ? `A sheet of ${a.parts.length} question${a.parts.length === 1 ? "" : "s"}, worth ${a.parts.reduce((n, p) => n + p.maxMarks, 0)} marks in total. The due date and publication below apply to all of them.`
            : isMcq
            ? "Multiple choice — marked by comparing the chosen options with the answer key."
            : "Programming — marked by running the student's code against the test cases."}{" "}
          {!isSheet && "The kind is fixed once a question exists; create a new one to change it."}
        </p>

        {/*
         * A sheet carries no statement of its own: the text, formats and marks
         * all belong to the questions below, and duplicating them here only
         * gave teachers two places to write the same thing.
         */}
        {!isSheet && (
        <label>
          <span>Problem statement (markdown)</span>
          <textarea rows={8} value={a.description} onChange={(e) => patch({ description: e.target.value })} />
        </label>
        )}

        {!isMcq && !isSheet && (
        <div className="grid-2">
          <label>
            <span>Input format</span>
            <textarea
              rows={4}
              value={a.inputFormat}
              placeholder={"First line: n, the number of elements.\nSecond line: n space-separated integers."}
              onChange={(e) => patch({ inputFormat: e.target.value })}
            />
          </label>
          <label>
            <span>Output format</span>
            <textarea
              rows={4}
              value={a.outputFormat}
              placeholder={"A single integer: the sum, followed by a newline."}
              onChange={(e) => patch({ outputFormat: e.target.value })}
            />
          </label>
        </div>
        )}
        {!isMcq && !isSheet && (
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>
          Say exactly what the program reads and prints. Output is compared against the expected
          text, so an extra prompt like <code>Enter a number:</code> fails every test — students
          need to know that from the statement, not from a failed submission.
        </p>
        )}

        <div className="grid-2">
          {!isSheet && (
            <label>
              <span>Maximum marks</span>
              <input
                type="number"
                min={1}
                value={a.maxMarks}
                onChange={(e) => patch({ maxMarks: Number(e.target.value) })}
              />
            </label>
          )}
          {isPart ? (
            <label>
              <span>Due date</span>
              <input
                value={a.parent?.dueAt ? new Date(a.parent.dueAt).toLocaleString() : "set on the sheet"}
                disabled
              />
            </label>
          ) : (
          <label>
            <span>Due date (optional)</span>
            <input
              type="datetime-local"
              value={dueLocal}
              onChange={(e) => patch({ dueAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
            />
          </label>
          )}
        </div>

        {!isMcq && (
        <div className="grid-2">
          <label>
            <span>Time limit per test (ms)</span>
            <input
              type="number"
              min={100}
              step={100}
              value={a.timeLimitMs}
              onChange={(e) => patch({ timeLimitMs: Number(e.target.value) })}
            />
          </label>
          <label>
            <span>Memory limit (MB)</span>
            <input
              type="number"
              min={4}
              value={Math.round(a.memoryLimitKb / 1024)}
              onChange={(e) => patch({ memoryLimitKb: Number(e.target.value) * 1024 })}
            />
          </label>
        </div>
        )}

        <div className="row" style={{ gap: 20, flexWrap: "wrap" }}>
          <label style={{ marginBottom: 0 }} className="row">
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={a.published}
              onChange={(e) => patch({ published: e.target.checked })}
            />
            <span style={{ marginBottom: 0 }}>Published (visible to students)</span>
          </label>
          <label style={{ marginBottom: 0 }} className="row" title="Other faculty in your institution can copy this into their own course. They get an independent copy; your version is never changed by theirs.">
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={a.libraryVisible}
              onChange={(e) => shareToLibrary(e.target.checked)}
            />
            <span style={{ marginBottom: 0 }}>Share to question library</span>
          </label>
          <label style={{ marginBottom: 0 }} className="row">
            <input
              type="checkbox"
              style={{ width: "auto" }}
              checked={a.allowLate}
              onChange={(e) => patch({ allowLate: e.target.checked })}
            />
            <span style={{ marginBottom: 0 }}>Allow late submissions</span>
          </label>
        </div>
      </div>

      {!isPart && (
        <SheetParts
          assignmentId={a.id}
          title={a.title}
          parts={a.parts}
          isSheet={isSheet}
          onChanged={reload}
          onError={setError}
          onStatus={setStatus}
        />
      )}

      {isSheet ? null : isMcq ? (
        <McqOptions
          assignmentId={a.id}
          options={a.options}
          onChange={(options) => setA((prev) => (prev ? { ...prev, options } : prev))}
          onError={setError}
        />
      ) : (
      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>STARTER CODE</div>
        <div style={{ height: 240, border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
          <Editor
            language="c"
            theme={editorTheme}
            value={a.starterCode}
            onChange={(v) => patch({ starterCode: v ?? "" })}
            options={{ fontSize: 13, minimap: { enabled: false }, tabSize: 4, automaticLayout: true }}
          />
        </div>
      </div>

      )}

      {!isMcq && !isSheet && (
      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          <strong>Test cases ({a.testCases.length})</strong>
          <div className="row">
            {dirtyCases.size > 0 && (
              <span className="muted" style={{ fontSize: 13 }}>
                {dirtyCases.size} edited — press Save
              </span>
            )}
            <button onClick={() => addTestCase()}>Add test case</button>
          </div>
        </div>

        {a.testCases.length === 0 && (
          <div className="muted">
            No test cases yet. Students cannot submit until at least one exists.
          </div>
        )}

        {a.testCases.map((tc, i) => (
          <div key={tc.id} className="card" style={{ background: "var(--bg)", marginBottom: 10 }}>
            <div className="row" style={{ marginBottom: 10, flexWrap: "wrap" }}>
              <input
                style={{ flex: 1, minWidth: 140 }}
                value={tc.name}
                onChange={(e) => updateTestCase(tc.id, { name: e.target.value })}
              />
              <select
                style={{ width: 150 }}
                value={tc.matchMode ?? "TRIMMED"}
                onChange={(e) => updateTestCase(tc.id, { matchMode: e.target.value })}
              >
                <option value="TRIMMED">Trimmed match</option>
                <option value="EXACT">Exact match</option>
                <option value="NUMERIC">Numeric tolerance</option>
              </select>
              <label className="row" style={{ marginBottom: 0, width: "auto", whiteSpace: "nowrap" }}>
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={tc.hidden}
                  onChange={(e) => updateTestCase(tc.id, { hidden: e.target.checked })}
                />
                <span style={{ marginBottom: 0 }}>Hidden</span>
              </label>
              <input
                type="number"
                min={1}
                style={{ width: 70 }}
                title="Weight"
                value={tc.weight}
                onChange={(e) => updateTestCase(tc.id, { weight: Number(e.target.value) })}
              />
              <button title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button
                title="Move down"
                disabled={i === a.testCases.length - 1}
                onClick={() => move(i, 1)}
              >
                ↓
              </button>
              <button title="Duplicate" onClick={() => addTestCase(tc)}>Duplicate</button>
              <button className="danger" onClick={() => deleteTestCase(tc.id)}>Delete</button>
              {dirtyCases.has(tc.id) && <span className="badge warn">edited</span>}
            </div>

            <div className="grid-2">
              <label style={{ marginBottom: 0 }}>
                <span>Input (stdin)</span>
                <textarea
                  rows={4}
                  value={tc.stdin ?? ""}
                  onChange={(e) => updateTestCase(tc.id, { stdin: e.target.value })}
                />
              </label>
              <label style={{ marginBottom: 0 }}>
                <span>Expected output</span>
                <textarea
                  rows={4}
                  value={tc.expectedStdout ?? ""}
                  onChange={(e) => updateTestCase(tc.id, { expectedStdout: e.target.value })}
                />
              </label>
            </div>
          </div>
        ))}
      </div>

      )}

      {!isMcq && !isSheet && (
        <Validator assignmentId={a.id} starterCode={a.starterCode} dirty={dirty} />
      )}
    </div>
  );
}

/**
 * The questions inside an assignment, each edited where it sits.
 *
 * An assignment with no parts is a single question, which is what one has
 * always been; adding a question turns it into a sheet and moves the existing
 * statement and test cases into question 1, so nothing written so far is lost
 * or duplicated.
 */
function SheetParts({
  assignmentId,
  title,
  parts,
  isSheet,
  onChanged,
  onError,
  onStatus,
}: {
  assignmentId: string;
  title: string;
  parts: AssignmentPart[];
  isSheet: boolean;
  onChanged: () => void;
  onError: (msg: string) => void;
  onStatus: (msg: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  /** The question to open on arrival: the one just added. */
  const [justAdded, setJustAdded] = useState<string | null>(null);

  async function addPart(kind: "PROGRAMMING" | "MCQ") {
    setBusy(true);
    try {
      const r = await api.post<{ part: { id: string }; converted: boolean }>(
        `/api/assignments/${assignmentId}/parts`,
        { kind }
      );
      setJustAdded(r.part.id);
      onStatus(
        r.converted
          ? "This is now a multi-part assignment. What you had written became Question 1."
          : "Question added — write it below."
      );
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not add the question.");
    } finally {
      setBusy(false);
    }
  }

  async function move(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= parts.length) return;
    const order = parts.map((p) => p.id);
    [order[index], order[target]] = [order[target], order[index]];
    try {
      await api.patch(`/api/assignments/${assignmentId}/parts/order`, { order });
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not reorder the questions.");
    }
  }

  async function remove(part: AssignmentPart) {
    if (!confirm(`Delete "${part.title}" from this assignment?`)) return;
    try {
      await api.del(`/api/assignments/${part.id}`);
      onStatus("Question removed.");
      onChanged();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Could not delete the question.");
    }
  }

  const total = parts.reduce((n, p) => n + p.maxMarks, 0);

  return (
    <div className="card">
      <div className="between" style={{ marginBottom: 12 }}>
        <strong>
          Questions ({parts.length})
          {isSheet && <span className="muted" style={{ fontWeight: 400 }}> · {total} marks</span>}
        </strong>
      </div>

      {!isSheet && (
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          This assignment holds a single question — its statement and test cases are below. Add
          another to turn it into a sheet, like a lab with three programs; what you have written
          becomes Question 1, and each question then carries its own statement, tests and marks.
        </p>
      )}

      {parts.map((p, i) => (
        <QuestionEditor
          key={p.id}
          questionId={p.id}
          index={i + 1}
          // Open on arrival when it is the only question -- a new assignment
          // otherwise shows a collapsed row and nowhere to type.
          defaultOpen={p.id === justAdded || parts.length === 1}
          onSummaryChange={onChanged}
          actions={
            <>
              <button title="Move up" disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
              <button
                title="Move down"
                disabled={i === parts.length - 1}
                onClick={() => move(i, 1)}
              >
                ↓
              </button>
              <button className="danger" onClick={() => remove(p)}>Delete</button>
            </>
          }
        />
      ))}

      <div className="add-question">
        <strong style={{ fontSize: 18, lineHeight: 1 }}>+</strong>
        <span className="muted" style={{ fontSize: 13 }}>Add a question</span>
        <button disabled={busy} onClick={() => addPart("PROGRAMMING")}>Programming</button>
        <button disabled={busy} onClick={() => addPart("MCQ")}>Multiple choice</button>
        {/* The third way to get a question, and usually the fastest: one that
            already has its statement, tests and marks written. The other two
            hand you a blank to fill in. */}
        <button disabled={busy} onClick={() => setPicking(true)}>From library</button>
      </div>

      {picking && (
        <LibraryImportModal
          into={{ sheetId: assignmentId }}
          title={title}
          importLabel="Add"
          onClose={() => setPicking(false)}
          onImported={(summary) => {
            onStatus(summary);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
